import { ActionTree } from "vuex";
import {
  RootState,
  FavoritesState,
  FavoriteFolderOperationResult,
  FavoriteOperationResult,
  FavoriteRecipeClips,
  FavoriteFolders,
  FavoriteFolderClips,
  FavoriteAddFolderRecipe,
  FavoriteDeleteFolderRecipe,
  FavoriteHistoryRecipes,
  FavoriteSendedHistoryRecipes
} from "@/types/favorites";
import store from "@/store";
import apiClient from "@/client/api-client";
import { API_URL_RECIPE } from "@/const/api-const";
import NativeClient from "@/client/native-client";
import { getApplianceListText } from "@/utils/search-util";

const actions: ActionTree<FavoritesState, RootState> = {
  // レシピ登録
  addRecipe: async ({ commit }, recipeId: number) => {
    // レシピ削除API呼び出し
    const res = await apiClient.post(API_URL_RECIPE.MYLIST_RECIPE_ADD, {
      // レシピID
      recipeId: recipeId
    });
    commit("updateAddRecipe", res.data);
  },
  updateAddRecipe: async ({ commit }, result: FavoriteOperationResult | null) => {
    commit("updateAddRecipe", result);
  },

  // レシピ削除
  deleteRecipe: async ({ commit }, recipeId: number) => {
    const result: FavoriteOperationResult | null =
      store.getters["favorites/deleteRecipe"];

    // レシピ削除API呼び出し
    const res = await apiClient.post(API_URL_RECIPE.MYLIST_RECIPE_DELETE, {
      recipeId: recipeId
    });
    commit("updateDeleteRecipe", res.data);
  },
  updateDeleteRecipe: async ({ commit }, result: FavoriteOperationResult | null) => {
    commit("updateDeleteRecipe", result);
  },

  // お気に入り一覧取得（トップ用、最近追加したもの）
  getRecentMyList: async ({ commit }) => {
    // お気に入りレシピ取得API呼び出し
    const apiParams = store.getters["common/getApiBaseParams"];
    const applianceText = getApplianceListText(apiParams.applianceList);
    const res = await apiClient.get(API_URL_RECIPE.MYLIST, {
      params: {
        // My家電ID
        myAppliance: (applianceText != null) ? applianceText : null,
        // 「ホーム > マイリスト」画面と「お気に入りを追加 > マイリストから選ぶ」 の画面で、表示するレシピを出し分けるために利用するパラメータ。
        needDlMenuId: false,
        // 取得開始位置（すでに取得済みの場合、取得した件数以降の開始位置を指定）
        start: 0,
        // 10件
        row: 10
      }
    });
    NativeClient.sendDebugLog("Action.ts getMyList recipeList.count = ");

    commit("updateRecentMyList", res.data);
  },
  updateRecentMyList: async ({ commit }, clips: FavoriteRecipeClips | null) => {
    commit("updateRecentMyList", clips);
  },

  // お気に入り一覧取得（追加読み込み対応）
  getMyList: async ({ commit }) => {
    const rowOneTime = 10;
    const apiParams = store.getters["common/getApiBaseParams"];

    const previous: FavoriteRecipeClips | null =
      store.getters["favorites/getMyList"];

    // 全取得済み判定と今回の取得開始位置
    let startPosition = 0;
    if (previous != null) {
      // 前回のデータが存在する
      if (previous.start + rowOneTime >= previous.totalCount) {
        // 既に全件取得ずみ
        return;
      }
      startPosition = previous.start + rowOneTime;
    }

    // お気に入りレシピ取得API呼び出し
    const applianceText = getApplianceListText(apiParams.applianceList);
    const res = await apiClient.get(API_URL_RECIPE.MYLIST, {
      params: {
        // My家電ID
        myAppliance: (applianceText != null) ? applianceText : null,
        // 「ホーム > マイリスト」画面と「お気に入りを追加 > マイリストから選ぶ」 の画面で、表示するレシピを出し分けるために利用するパラメータ。
        needDlMenuId: false,
        // 取得開始位置（すでに取得済みの場合、取得した件数以降の開始位置を指定）
        start: startPosition,
        // 10件
        row: rowOneTime
      }
    });

    commit("updateMyList", res.data);
  },
  updateMyList: async ({ commit }, clips: FavoriteRecipeClips | null) => {
    commit("updateMyList", clips);
  },

  // フォルダ一覧
  getFolderList: async ({ commit }) => {
    const apiParams = store.getters["common/getApiBaseParams"];
    // お気に入りフォルダ一取得API呼び出し
    const applianceText = getApplianceListText(apiParams.applianceList);
    const res = await apiClient.get(API_URL_RECIPE.MYLIST_FOLDER_LIST, {
      params: {
        myAppliance: (applianceText != null) ? applianceText : null
      }
    });

    commit("updateFolderList", res.data);
  },
  updateFolderList: async ({ commit }, folders: FavoriteFolders | null) => {
    commit("updateFolderList", folders);
  },

  // フォルダ登録
  registFolder: async ({ commit }, name: string) => {
    NativeClient.sendDebugLog("Action.ts registFolder name = " + name);
    // お気に入りフォルダ登録API呼び出し
    const res = await apiClient.post(API_URL_RECIPE.MYLIST_FOLDER_REGIST, {
      // フォルダ名
      name: name
    });
    commit("updateRegistFolder", res.data);
  },
  updateRegistFolder: async ({ commit }, result: FavoriteFolderOperationResult | null) => {
    commit("updateRegistFolder", result);
  },

  // フォルダ内のレシピ取得
  getFolderRecipeList: async ({ commit }, folderId: number) => {
    const rowOneTime = 10;
    const apiParams = store.getters["common/getApiBaseParams"];
    // フォルダIDをパスに設定
    NativeClient.sendDebugLog("Action.ts getFolderRecipeList folderId = " + folderId);
    const apiPath = API_URL_RECIPE.MYLIST_FOLDER_RECIPE_LIST.replace("$folderId", folderId.toString());
    NativeClient.sendDebugLog("Action.ts getFolderRecipeList path = " + apiPath);

    const previous: FavoriteFolderClips | null =
      store.getters["favorites/getFolderRecipeList"];

    // 全取得済み判定と今回の取得開始位置
    let startPosition = 0;
    if (previous != null) {
      // 前回のデータが存在する
      if (previous.start + rowOneTime >= previous.totalCount) {
        // 既に全件取得ずみ
        return;
      }
      startPosition = previous.start + rowOneTime;
    }

    // お気に入りレシピ取得API呼び出し
    const applianceText = getApplianceListText(apiParams.applianceList);
    const res = await apiClient.get(apiPath, {
      params: {
        myAppliance: (applianceText != null) ? applianceText : null,
        start: startPosition,
        row: rowOneTime
      }
    });
    NativeClient.sendDebugLog("Action.ts getFolderRecipeList response = " + JSON.stringify(res.data));
    commit("updateFolderRecipeList", res.data);
  },
  updateFolderRecipeList: async ({ commit }, clips: FavoriteFolderClips | null) => {
    commit("updateFolderRecipeList", clips);
  },

  // お気に入りフォルダ更新（名前変更）
  renameFolder: async ({ commit }, params: { folderId: number, folderName: string }) => {
    // フォルダIDをパスに設定
    const apiPath = API_URL_RECIPE.MYLIST_FOLDER_RENAME.replace("$folderId", params.folderId.toString());

    // お気に入りレシピ取得API呼び出し
    const res = await apiClient.put(apiPath, {
      // フォルダ名
      name: params.folderName
    });

    commit("updateRenameFolder", res.data);
  },
  updateRenameFolder: async ({ commit }, result: FavoriteFolderOperationResult | null) => {
    commit("updateRenameFolder", result);
  },

  // お気に入りフォルダ削除
  deleteFolder: async ({ commit }, folderId: number) => {
    // フォルダIDをパスに設定
    const apiPath = API_URL_RECIPE.MYLIST_FOLDER_DELETE.replace("$folderId", folderId.toString());

    // お気に入りレシピ取得API呼び出し
    const res = await apiClient.delete(apiPath);

    commit("updateDeleteFolder", res.data);
  },
  updateDeleteFolder: async ({ commit }, result: FavoriteFolderOperationResult | null) => {
    commit("updateDeleteFolder", result);
  },

  // お気に入りフォルダへのレシピ追加
  addFolderRecipe: async ({ commit }, params: { folderId: number, recipes: FavoriteAddFolderRecipe[] }) => {
    // フォルダIDをパスに設定
    for (const recipeId of params.recipes) {
      NativeClient.sendDebugLog("Action.ts addFolderRecipe recipeIds = " + recipeId);
    }
    const apiPath = API_URL_RECIPE.MYLIST_FOLDER_ADD_RECIPE.replace("$folderId", params.folderId.toString());
    NativeClient.sendDebugLog("Action.ts addFolderRecipe path = " + apiPath);

    // お気に入りレシピ取得API呼び出し
    const res = await apiClient.post(apiPath, {
      recipes: params.recipes
    });
    NativeClient.sendDebugLog("Action.ts addFolderRecipe response = " + JSON.stringify(res.data));
    commit("updateAddFolderRecipe", res.data);
  },
  updateAddFolderRecipe: async ({ commit }, result: FavoriteFolderOperationResult | null) => {
    commit("updateAddFolderRecipe", result);
  },

  // お気に入りフォルダ削除
  deleteFolderRecipe: async ({ commit }, params: { folderId: number, recipeIds: number[] }) => {
    // フォルダIDをパスに設定
    NativeClient.sendDebugLog("Action.ts deleteFolderRecipe folderId = " + params.folderId + ", recipeId = " + params.recipeIds[0]);
    const apiPath = API_URL_RECIPE.MYLIST_FOLDER_REMOVE_RECIPE.replace("$folderId", params.folderId.toString());
    NativeClient.sendDebugLog("Action.ts deleteFolderRecipe path = " + apiPath);

    const recipeIdArray: FavoriteDeleteFolderRecipe[] = [];
    for (const recipeId of params.recipeIds) {
      const element = new FavoriteDeleteFolderRecipe(recipeId);
      recipeIdArray.push(element);
    }

    // お気に入りレシピ取得API呼び出し
    const res = await apiClient.post(apiPath, {
      recipes: recipeIdArray
    });
    NativeClient.sendDebugLog("Action.ts deleteFolderRecipe end ");
    commit("updateDeleteFolderRecipe", res.data);
  },
  updateDeleteFolderRecipe: async ({ commit }, result: FavoriteFolderOperationResult | null) => {
    commit("updateDeleteFolderRecipe", result);
  },

  // お気に入りフォルダ削除
  moveFolder: async ({ commit }, params: { folderId: number, position: number }) => {
    // フォルダIDをパスに設定
    const apiPath = API_URL_RECIPE.MYLIST_FOLDER_SORT.replace("$folderId", params.folderId.toString());

    // お気に入りレシピ取得API呼び出し
    const res = await apiClient.put(apiPath, {
      position: params.position
    });

    commit("updateMoveFolder", res.data);
  },
  updateMoveFolder: async ({ commit }, result: FavoriteOperationResult | null) => {
    commit("updateMoveFolder", result);
  },
  updateFolderName: async ({ commit }, folderName: string | null) => {
    commit("updateFolderName", folderName);
  },
  // トップ送信履歴
  getTopSendedHistory: async ({ commit }) => {
    const rowOneTime = 10;

    const previous: FavoriteSendedHistoryRecipes | null = store.getters["favorites/getSendedHistory"];
    // お気に入りレシピ取得API呼び出し
    const res = await apiClient.get(API_URL_RECIPE.USER_DOWNLOAD_HISTORY, {
      params: {
        // 集約
        groupBy: "both",
        // 取得開始位置（すでに取得済みの場合、取得した件数以降の開始位置を指定）
        start: 0,
        // 10件
        row: rowOneTime
      }
    });
    NativeClient.sendDebugLog("Action.ts getTopSendedHistory start = " + res.data.start + ", totalCount = " + res.data.totalCount);

    // クリアしてから保存する
    commit("updateSendedHistory", null);
    commit("updateSendedHistory", res.data);
  },
  updateTopSendedHistory: async ({ commit }, clips: FavoriteSendedHistoryRecipes | null) => {
    commit("updateSendedHistory", clips);
  },

  // トップの閲覧履歴
  getTopViewedHistory: async ({ commit }) => {
    const rowOneTime = 10;

    const previous: FavoriteHistoryRecipes | null = store.getters["favorites/getViewedHistory"];

    // 閲覧履歴
    const res = await apiClient.get(API_URL_RECIPE.USER_HISTORY, {
      params: {
        // 集約
        groupBy: "both",
        // 取得開始位置（すでに取得済みの場合、取得した件数以降の開始位置を指定）
        start: 0,
        // 10件
        row: rowOneTime
      }
    });
    NativeClient.sendDebugLog("Action.ts getTopViewedHistory start = " + res.data.start + ", totalCount = " + res.data.totalCount);

    // クリアしてから保存する
    commit("updateViewedHistory", null);
    commit("updateViewedHistory", res.data);
  },
  updateTopViewedHistory: async ({ commit }, clips: FavoriteHistoryRecipes | null) => {
    commit("updateViewedHistory", clips);
  },

  // 送信履歴
  getSendedHistory: async ({ commit }) => {
    const rowOneTime = 10;
    NativeClient.sendDebugLog("Action.ts getSendedHistory");

    const previous: FavoriteSendedHistoryRecipes | null = store.getters["favorites/getSendedHistory"];

    // 全取得済み判定と今回の取得開始位置
    let startPosition = 0;
    if (previous != null) {
      // 前回のデータが存在する
      NativeClient.sendDebugLog("Action.ts getSendedHistory start = " + previous.start + ", totalCount = " + previous.totalCount);
      if ((previous.start + rowOneTime >= previous.totalCount) || (previous.recipes.length >= previous.totalCount)) {
        // 既に全件取得ずみ
        return;
      }
      startPosition = previous.start + rowOneTime;
    } else {
      NativeClient.sendDebugLog("Action.ts getSendedHistory null");
    }

    // お気に入りレシピ取得API呼び出し
    const res = await apiClient.get(API_URL_RECIPE.USER_DOWNLOAD_HISTORY, {
      params: {
        // 取得開始位置（すでに取得済みの場合、取得した件数以降の開始位置を指定）
        start: startPosition,
        // 10件
        row: rowOneTime
      }
    });

    commit("updateSendedHistory", res.data);
  },
  updateSendedHistory: async ({ commit }, clips: FavoriteSendedHistoryRecipes | null) => {
    commit("updateSendedHistory", clips);
  },

  // 送信履歴
  getViewedHistory: async ({ commit }) => {
    const rowOneTime = 10;

    const previous: FavoriteHistoryRecipes | null = store.getters["favorites/getViewedHistory"];

    // 全取得済み判定と今回の取得開始位置
    let startPosition = 0;
    if (previous != null) {
      // 前回のデータが存在する
      NativeClient.sendDebugLog("Action.ts getViewedHistory start = " + previous.start + ", totalCount = " + previous.totalCount);
      if ((previous.start + rowOneTime >= previous.totalCount) || (previous.recipes.length >= previous.totalCount)) {
        // 既に全件取得ずみ
        return;
      }
      startPosition = previous.start + rowOneTime;
    }

    // お気に入りレシピ取得API呼び出し
    const res = await apiClient.get(API_URL_RECIPE.USER_HISTORY, {
      params: {
        // 集約
        groupBy: "both",
        // 取得開始位置（すでに取得済みの場合、取得した件数以降の開始位置を指定）
        start: startPosition,
        // 10件
        row: rowOneTime
      }
    });

    commit("updateViewedHistory", res.data);
  },
  updateViewedHistory: async ({ commit }, clips: FavoriteHistoryRecipes | null) => {
    commit("updateViewedHistory", clips);
  },
  updateSelectedTab: ({ commit }, selected: number) => {
    commit("updateSelectedTab", selected);
  }
};

export default actions;
